/**
 * SEO Schema Generators for Vastré By Vijh
 * Snitch-style SEO implementation with JSON-LD schemas
 */

/**
 * Generate Product Schema for individual product pages
 * @param {Object} product - Product data
 * @returns {Object} JSON-LD Product schema
 */
export const generateProductSchema = (product) => {
  if (!product) return null;

  const schema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.name,
    "image": product.imageURLs || [],
    "description": product.description || `${product.name} by Vastré By Vijh - Premium men's fashion`,
    "brand": {
      "@type": "Brand",
      "name": "Vastré By Vijh"
    },
    "offers": {
      "@type": "Offer",
      "url": `https://vastrebyvijh.com/product/${product.id}`,
      "priceCurrency": "INR",
      "price": product.basePrice || product.price,
      "availability": product.status === "In Stock" 
        ? "https://schema.org/InStock" 
        : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "Vastré By Vijh"
      }
    }
  };

  // Add aggregateRating if reviews exist
  if (product.rating && product.reviewCount) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": product.rating,
      "reviewCount": product.reviewCount
    };
  }

  return schema;
};

/**
 * Generate Breadcrumb Schema
 * @param {Array} breadcrumbs - Array of {name, url} objects
 * @returns {Object} JSON-LD BreadcrumbList schema
 */
export const generateBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  };
};

/**
 * Generate Collection/Category Schema
 * @param {Object} category - Category data
 * @param {Array} products - Products in category
 * @returns {Object} JSON-LD CollectionPage schema
 */
export const generateCollectionSchema = (category, products = []) => {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": category.name,
    "description": category.description || `Shop ${category.name} by Vastré By Vijh`,
    "url": `https://vastrebyvijh.com/collections?category=${category.id}`,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": products.length,
      "itemListElement": products.slice(0, 10).map((product, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://vastrebyvijh.com/product/${product.id}`
      }))
    }
  };
};

/**
 * Generate FAQ Schema
 * @param {Array} faqs - Array of {question, answer} objects
 * @returns {Object} JSON-LD FAQPage schema
 */
export const generateFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

/**
 * Generate SEO meta tags for product pages
 * @param {Object} product - Product data
 * @returns {Object} SEO meta data
 */
export const generateProductSEO = (product) => {
  if (!product) return null;

  // Extract category name for better keywords
  const categoryName = product.categoryName || '';
  
  // Generate keyword-rich title (Snitch-style)
  const title = `${product.name} | Premium ${categoryName} for Men | Vastré By Vijh`;
  
  // Generate compelling description (max 160 chars)
  const description = `Shop ${product.name} by Vastré By Vijh. ${categoryName ? `Premium ${categoryName.toLowerCase()}` : 'Luxury menswear'} with custom fit and superior craftsmanship. ₹${product.basePrice || product.price}`;
  
  // Generate relevant keywords
  const keywords = [
    'vastrebyvijh',
    product.name.toLowerCase(),
    categoryName.toLowerCase(),
    'premium menswear',
    'custom tailored',
    'luxury men\'s fashion India',
    'designer menswear online'
  ].filter(Boolean).join(', ');

  return {
    title,
    description: description.substring(0, 160), // Ensure max 160 chars
    keywords,
    image: product.imageURLs?.[0] || '',
    url: `https://vastrebyvijh.com/product/${product.id}`,
    type: 'product',
    structuredData: generateProductSchema(product)
  };
};

/**
 * Generate SEO meta tags for collection pages
 * @param {Object} category - Category data
 * @param {Array} products - Products in category
 * @returns {Object} SEO meta data
 */
export const generateCollectionSEO = (category, products = []) => {
  const title = `${category.name} for Men | Premium ${category.name} Online | Vastré By Vijh`;
  const description = `Shop premium ${category.name.toLowerCase()} for men by Vastré By Vijh. Custom-made, luxury menswear with perfect fit. ${products.length}+ styles available online.`;
  
  const keywords = [
    'vastrebyvijh',
    `${category.name.toLowerCase()} for men`,
    `premium ${category.name.toLowerCase()}`,
    `${category.name.toLowerCase()} online India`,
    'luxury menswear',
    'custom tailored clothing'
  ].join(', ');

  return {
    title,
    description: description.substring(0, 160),
    keywords,
    image: category.imageURL || products[0]?.imageURLs?.[0] || '',
    url: `https://vastrebyvijh.com/collections?category=${category.id}`,
    type: 'website',
    structuredData: generateCollectionSchema(category, products)
  };
};

/**
 * Generate keyword-rich meta tags (Snitch-style)
 * Focuses on high-traffic, conversion-oriented keywords
 */
export const SNITCH_STYLE_KEYWORDS = {
  // Product-specific
  shirts: 'men\'s shirts, formal shirts, casual shirts, premium shirts online, custom shirts India',
  kurta: 'designer kurta for men, ethnic wear, kurta online, wedding kurta, premium kurta',
  pants: 'men\'s trousers, formal pants, custom pants, tailored trousers online',
  blazer: 'men\'s blazers, formal blazers, designer blazers, premium blazers India',
  suits: 'custom suits, tailored suits, wedding suits, bespoke suits India',
  sherwani: 'designer sherwani, wedding sherwani, premium sherwani online',
  
  // Generic high-traffic keywords
  generic: 'luxury men\'s fashion, premium menswear, online men\'s clothing, designer menswear India, custom tailored clothing, bespoke fashion, vastrebyvijh'
};

/**
 * Get category-specific keywords
 * @param {string} categoryId - Category ID
 * @returns {string} Comma-separated keywords
 */
export const getCategoryKeywords = (categoryId) => {
  return SNITCH_STYLE_KEYWORDS[categoryId] || SNITCH_STYLE_KEYWORDS.generic;
};
